import { can, type Principal } from '@selfon/domain/browser';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { api, type Me } from '../api';
import { useAuth } from '../auth/AuthContext';
import mark from '../assets/selfon-mark.png';
import { ROLE_LABEL, time } from '../format';
import { MainNav, type NavItem } from './MobileNav';
import { useLoad } from './ui';
import { ThemeSwitch } from '../theme';

/** 서버의 /me 결과로 화면용 권한 판단(최종 판단은 항상 서버가 한다). */
export function principalOf(me: Me): Principal {
  return { userId: me.userId, role: me.role, centerId: me.centerId, clientScope: 'ALL', impersonation: me.impersonation ?? undefined, childMode: me.childMode ?? undefined };
}

/** 화면 틀: 데스크톱은 왼쪽 메뉴, 휴대폰은 아래 고정 메뉴(design-v3.css). */
export function Layout() {
  const { me, signOut, impersonation, endImpersonation } = useAuth();
  const center = useLoad(() => (me?.centerId ? api.center(me.centerId) : Promise.resolve(null)), [me?.centerId]);
  const { pathname } = useLocation();
  // 화면을 옮길 때마다 새 답변·신청이 있는지 다시 본다
  const teacherAlerts = useLoad(() => (me && !me.childMode && !me.impersonation && me.role !== 'HQ_ADMIN' && me.role !== 'STUDENT' ? api.teacherAlerts() : Promise.resolve(null)), [me?.userId, pathname]);
  if (!me) return null;
  const p = principalOf(me);
  const inCenter = Boolean(me.centerId);
  const ownCenter = me.role === 'CENTER_DIRECTOR' && !me.impersonation;
  const todos = can(p, 'todo:use') && { to: '/todos', label: '할 일 관리' };
  // 학생 계정은 과제와 할 일만, 자녀 모드는 학생 화면 하나만
  const links = me.role === 'STUDENT' ? (me.childMode ? [] : [{ to: '/student', label: '오늘의 과제' }, todos as NavItem]) : [
    me.role === 'GUARDIAN' && { to: '/family', label: '우리 아이' },
    inCenter && me.role !== 'GUARDIAN' && { to: '/home', label: '오늘' },
    inCenter && !me.impersonation && (me.role==='CENTER_DIRECTOR'||me.role==='THERAPIST') && { to:'/notes',label:'회기 기록 · SOAP' },
    inCenter && can(p, 'session:read') && { to: '/sessions', label: '일정·출결' },
    me.role === 'GUARDIAN' && { to: '/courses', label: '센터 수업' },
    ((me.role === 'GUARDIAN' && !me.childMode) || (inCenter && (me.role === 'CENTER_DIRECTOR' || me.role === 'THERAPIST')) || (me.role === 'HQ_ADMIN' && !me.impersonation)) && { to: '/feed', label: '오늘 있었던 일' },
    me.role === 'GUARDIAN' && { to: '/teachers', label: '선생님 찾기', badge: teacherAlerts.data?.unread },
    inCenter && can(p, 'ledger:read') && { to: '/billing', label: '수납 현황' },
    inCenter && can(p, 'closing:manage') && { to: '/accounting', label: '결산 회계 세무' },
    inCenter && can(p, 'client:read') && { to: '/clients', label: '이용자' },
    inCenter && can(p, 'voucher:audit') && { to: '/voucher', label: '바우처 대조' },
    inCenter && can(p, 'task:read') && { to: '/tasks', label: me.role === 'GUARDIAN' ? '오늘의 과제' : '치료 과제' },
    me.role !== 'HQ_ADMIN' && todos,
    inCenter && can(p, 'course:manage') && { to: '/center-courses', label: '센터 수업 관리' },
    inCenter && can(p, 'course:manage') && { to: '/programs', label: '프로그램 관리' },
    inCenter && can(p, 'payroll:read') && { to: '/my-payslips', label: '내 임금명세서' },
    (inCenter || me.profile?.independent) && can(p, 'teacher:profile') && { to: '/teacher-desk', label: '선생님 소개', badge: teacherAlerts.data?.pending },
    inCenter && can(p, 'notice:read') && { to: '/notices', label: '공지·문의' },
    inCenter && (can(p, 'contract:request') || Boolean(me.impersonation)) && { to: '/settings', label: '센터 설정' },
    me.role === 'HQ_ADMIN' && !me.impersonation && { to: '/hq', label: '센터 관리', end: true },
    me.role === 'HQ_ADMIN' && !me.impersonation && { to: '/hq/contracts', label: '계약 심사' },
    me.role === 'HQ_ADMIN' && !me.impersonation && { to: '/hq/messaging', label: '알림톡 승인' },
    me.role === 'HQ_ADMIN' && !me.impersonation && { to: '/hq/teachers', label: '개인 선생님 승인' },
    me.role === 'HQ_ADMIN' && !me.impersonation && { to: '/hq/notices', label: '공지·문의' },
    me.role === 'HQ_ADMIN' && todos,
    can(p, 'audit:read') && { to: '/audit', label: '감사 로그' },
  ].filter(Boolean) as NavItem[];

  const sidebar = useSidebarWidth();
  const fold = useSidebarFold();
  const student = me.role === 'STUDENT';
  const folded = fold.folded && !student;

  return (
    <div className={folded ? 'shell sidebar-folded' : 'shell'} style={student ? undefined : ({ '--sidebar-w': `${sidebar.width}px` } as CSSProperties)}>
      {folded && (
        // 메뉴를 접었을 때 왼쪽 위에 남는 펼치기 버튼(1100px 이상에서만 보인다)
        <button type="button" ref={fold.openRef} className="sidebar-fold sidebar-unfold" aria-label="왼쪽 메뉴 펼치기" aria-expanded={false}
          title={`메뉴 펼치기 (${FOLD_KEY})`} onClick={fold.toggle}><ChevronIcon dir="right" /></button>
      )}
      {me.impersonation && impersonation && (
        <div className="imp-banner" role="status">
          <span>
            <strong>본사 대리접속 중</strong> · {center.data?.center.name ?? impersonation.centerId} ·{' '}
            {impersonation.mode === 'READ' ? '읽기 전용' : '쓰기 허용'} · {time(impersonation.expiresAt)}까지 · 모든 동작이 센터 감사 로그에 남습니다
          </span>
          <button type="button" className="btn btn-light" onClick={endImpersonation}>대리접속 종료</button>
        </div>
      )}
      <header className="topbar">
        <div className="brand">
          <img className="logo-img" src={mark} alt="" width={30} height={30} />
          {/* 원장님 화면은 센터 이름이 로고 자리에 온다. 다른 역할·대리접속은 SelfOn · 센터 */}
          {ownCenter
            ? <span className="center-brand"><FitTitle text={center.data?.center.name ?? '센터'} /></span>
            : <span><strong>SelfOn</strong>{center.data && <span className="brand-center"><span className="brand-sep"> · </span>{center.data.center.name}</span>}</span>}
        </div>
        <MainNav items={links} />
        <div className="who">
          <span className={me.childMode ? 'role role-child' : 'role'}>{me.childMode ? `자녀 모드 · ${me.childMode.childName}` : `${me.name ? `${me.name} · ` : ''}${ROLE_LABEL[me.impersonation ? 'HQ_ADMIN' : me.role]}`}</span>
          {!me.childMode && !student && (
            <div className="who-links"><Link to="/terms">이용약관</Link><Link to="/privacy">개인정보처리방침</Link><Link to="/refund">환불 정책</Link><Link to="/support">고객센터</Link>{me.role !== 'HQ_ADMIN' && !me.impersonation && <Link to="/account">내 계정</Link>}</div>
          )}
          <ThemeSwitch compact />
          <button type="button" className="btn btn-ghost" onClick={() => signOut()}>로그아웃</button>
        </div>
        {!student && (
          // 넓은 화면(1100px 이상)의 왼쪽 메뉴 오른쪽 가장자리: 끌기·더블클릭·방향키로 너비 조절
          <div className="sidebar-resizer" role="separator" aria-orientation="vertical" aria-label="왼쪽 메뉴 너비 조절" tabIndex={0}
            aria-valuemin={SIDEBAR.min} aria-valuemax={SIDEBAR.max} aria-valuenow={sidebar.width}
            title="끌어서 메뉴 너비 조절 · 더블클릭하면 기본 크기" onPointerDown={sidebar.onPointerDown} onDoubleClick={sidebar.reset} onKeyDown={sidebar.onKeyDown} />
        )}
      </header>
      {!student && !folded && (
        // 접기 버튼은 메뉴 오른쪽 가장자리에 걸친 작은 손잡이로 둔다(센터 이름 자리를 차지하지 않게)
        <button type="button" ref={fold.closeRef} className="sidebar-fold sidebar-edge" aria-label="왼쪽 메뉴 접기" aria-expanded
          title={`메뉴 접기 (${FOLD_KEY})`} onClick={fold.toggle}><ChevronIcon dir="left" /></button>
      )}
      <main id="main" className="main" tabIndex={-1}><Outlet /></main>
    </div>
  );
}

const SIDEBAR = { min: 224, max: 360, initial: 252, key: 'selfon.sidebar.width' } as const;
const clampW = (w: number) => Math.round(Math.min(SIDEBAR.max, Math.max(SIDEBAR.min, w)));

/** 왼쪽 메뉴 너비(224~360px). 이 브라우저에만 기억한다(저장할 수 없으면 이번 창에서만). */
function useSidebarWidth() {
  const [width, setWidth] = useState<number>(() => {
    try { const v = Number(localStorage.getItem(SIDEBAR.key)); return v ? clampW(v) : SIDEBAR.initial; } catch { return SIDEBAR.initial; }
  });
  useEffect(() => { try { localStorage.setItem(SIDEBAR.key, String(width)); } catch { /* 저장 불가 환경 */ } }, [width]);
  const reset = useCallback(() => setWidth(SIDEBAR.initial), []);
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const x0 = e.clientX, w0 = width;
    document.body.classList.add('sidebar-resizing');
    const move = (ev: globalThis.PointerEvent) => setWidth(clampW(w0 + ev.clientX - x0));
    const up = () => {
      document.body.classList.remove('sidebar-resizing');
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 24 : 8;
    if (e.key === 'ArrowLeft') setWidth((w) => clampW(w - step));
    else if (e.key === 'ArrowRight') setWidth((w) => clampW(w + step));
    else if (e.key === 'Home') reset();
    else return;
    e.preventDefault();
  };
  return { width, reset, onPointerDown, onKeyDown };
}

const FOLD_STORE = 'selfon.sidebar.folded';
const FOLD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘\\' : 'Ctrl+\\';

/** 왼쪽 메뉴 접기·펼치기. 이 브라우저에만 기억하고, ⌘\ 또는 Ctrl+\ 로도 바꾼다. */
function useSidebarFold() {
  const [folded, setFolded] = useState<boolean>(() => {
    try { return localStorage.getItem(FOLD_STORE) === '1'; } catch { return false; }
  });
  const openRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const moved = useRef(false); // 사용자가 바꿨을 때만 반대쪽 버튼으로 초점을 옮긴다(처음 열 때는 그대로)
  const toggle = useCallback(() => { moved.current = true; setFolded((f) => !f); }, []);
  useEffect(() => {
    try { localStorage.setItem(FOLD_STORE, folded ? '1' : '0'); } catch { /* 저장 불가 환경 */ }
    if (moved.current) (folded ? openRef : closeRef).current?.focus();
  }, [folded]);
  useEffect(() => {
    // 한글 자판에서는 \ 자리가 ₩ 로 찍히므로 글자 대신 키 위치(code)로 본다
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.code !== 'Backslash' || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (!matchMedia('(min-width:1100px)').matches) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);
  return { folded, toggle, openRef, closeRef };
}

export function ChevronIcon({ dir, size = 16 }: { dir: 'left' | 'right'; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={dir === 'left' ? 'M14.5 6l-6 6 6 6' : 'M9.5 6l6 6-6 6'} />
    </svg>
  );
}

/** 센터 이름: 한 줄을 유지하며 넘치면 글자 크기를 줄인다(기본 20px, 휴대폰 19px, 최소 12px). */
function FitTitle({ text }: { text: string }) {
  const ref = useRef<HTMLElement>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    let size = matchMedia('(max-width:760px)').matches ? 19 : 20;
    el.style.fontSize = `${size}px`;
    while (el.scrollWidth > el.clientWidth + 1 && size > 12) { size -= 0.5; el.style.fontSize = `${size}px`; }
  }, []);
  useLayoutEffect(fit, [text, fit]);
  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    void document.fonts?.ready.then(fit); // 글꼴이 늦게 적용되면 폭이 바뀐다
    return () => ro.disconnect();
  }, [fit]);
  return <strong ref={ref} className="center-brand-title" title={text}>{text}</strong>;
}
